import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel } from '@mui/material';
import { getAllContentSchedules, createContentSchedule, deleteContentSchedule } from '../../../../services/contentApiFn';
import { handleOpenSnackBar } from '../../../../utils';
import PaperContainer from '../../../../components/Containers/PaperContainer';
import DynamicPaginatedTable from '../../../../components/Tables/DynamicPaginatedTable';
import GlobalButton from '../../../../components/CustomButton';
import DeleteAlert from '../../../../components/Alert/DeleteAlert';

function AutomationSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const openDeleteDialog = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    cronExpression: '0 9 * * 1', // Mon 9AM
    productSelectionStrategy: 'random',
    autoApprove: false,
    channels: ['facebook', 'instagram', 'blog']
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const res = await getAllContentSchedules();
      if (res?.data?.status === 'success') {
        setSchedules(res.data.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formattedSchedules = schedules.map(s => ({
    ...s,
    autoApproveLabel: s.autoApprove ? 'Yes' : 'No',
  }));

  const handleCreate = async () => {
    try {
      if (!formData.name) return handleOpenSnackBar('error', 'Name is required');
      const res = await createContentSchedule(formData);
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Schedule created successfully');
        setOpenDialog(false);
        fetchSchedules();
      }
    } catch (error) {
      handleOpenSnackBar('error', 'Failed to create schedule');
    }
  };

  const handleOpenDeleteDialog = ({ row }) => {
    openDeleteDialog.current.openDialog({
      data: row,
      warningText: 'Are you sure you want to delete this automation schedule?',
    });
  };

  const handleDelete = async (params) => {
    try {
      await deleteContentSchedule(params.data._id);
      handleOpenSnackBar('success', 'Schedule deleted');
      openDeleteDialog.current.closeDialog();
      fetchSchedules();
    } catch (error) {
      handleOpenSnackBar('error', 'Failed to delete schedule');
    }
  };

  return (
    <PaperContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Automation Schedules</Typography>
        <GlobalButton btnText="Create Schedule" handleClickBtn={() => setOpenDialog(true)} />
      </Box>

      <DynamicPaginatedTable
        isLoading={loading}
        tableData={formattedSchedules}
        tableHeader={['name', 'cronExpression', 'strategy', 'autoApprove', 'actions']}
        displayRows={['name', 'cronExpression', 'productSelectionStrategy', 'autoApproveLabel', 'actions']}
        showActions
        showDelete
        handleDelete={handleOpenDeleteDialog}
      />

      {/* Create Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Automation Rule</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Schedule Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Weekly Highlight"
            />
            <TextField
              label="Cron Expression"
              fullWidth
              value={formData.cronExpression}
              onChange={(e) => setFormData({ ...formData, cronExpression: e.target.value })}
              placeholder="0 9 * * 1"
            />
            <FormControl fullWidth>
              <InputLabel>Product Selection Strategy</InputLabel>
              <Select
                value={formData.productSelectionStrategy}
                onChange={(e) => setFormData({ ...formData, productSelectionStrategy: e.target.value })}
                label="Product Selection Strategy"
              >
                <MenuItem value="random">Random Product</MenuItem>
                <MenuItem value="newest">Newest Product</MenuItem>
                <MenuItem value="featured">Featured Product</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={formData.autoApprove} onChange={(e) => setFormData({ ...formData, autoApprove: e.target.checked })} />}
              label="Automatically Approve & Publish Generated Content"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" sx={{ bgcolor: '#7922D8' }}>Create</Button>
        </DialogActions>
      </Dialog>

      <DeleteAlert ref={openDeleteDialog} handleDelete={handleDelete} />
    </PaperContainer>
  );
}

export default AutomationSchedules;
